import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { NotificationProvider } from './contexts/NotificationContext';
import Navbar from './components/Navbar';
import Landing from './pages/Landing';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import Assessment from './pages/Assessment';
import Report from './pages/Report';
import AdminDashboard from './pages/AdminDashboard';
import SuperAdminDashboard from './pages/SuperAdminDashboard';
import QuestionCMS from './pages/QuestionCMS';
import Resources from './pages/Resources';
import Findings from './pages/Findings';
import SharedResults from './pages/SharedResults';
import ClientDashboard from './pages/ClientDashboard';
import SSPList from './pages/SSPList';
import SSPEditor from './pages/SSPEditor';
import VulnerabilityDashboard from './pages/VulnerabilityDashboard';
import SecurityScorecard from './pages/SecurityScorecard';
import ComplianceCalendar from './pages/ComplianceCalendar';
import IncidentTracker from './pages/IncidentTracker';
import RiskRegister from './pages/RiskRegister';
import PolicyLibrary from './pages/PolicyLibrary';
import AssetInventory from './pages/AssetInventory';
import PentestDashboard from './pages/PentestDashboard';
import SelfAssessment from './pages/SelfAssessment';
import SelfAssessmentResults from './pages/SelfAssessmentResults';
import OrganizationSettings from './pages/OrganizationSettings';
import CreateOrganization from './pages/CreateOrganization';
import AlertsPage from './pages/AlertsPage';
import TrainingDashboard from './pages/TrainingDashboard';
import VendorRiskDashboard from './pages/VendorRiskDashboard';
import DarkWebDashboard from './pages/DarkWebDashboard';
import ControlMappingDashboard from './pages/ControlMappingDashboard';
import EvidenceLibrary from './pages/EvidenceLibrary';
import DataRoomPage from './pages/DataRoomPage';
import NewsPage from './pages/NewsPage';
import VirtualCISO from './pages/VirtualCISO';
import TicketDashboard from './pages/TicketDashboard';
import QBR from './pages/QBR';
import MSPPortfolio from './pages/MSPPortfolio';
import ProposalGenerator from './pages/ProposalGenerator';
import ContractManagement from './pages/ContractManagement';
import SspReport from './pages/SspReport';
import SspFullReport from './pages/SspFullReport';
import MySspReports from './pages/MySspReports';
import ReadinessDashboard from './pages/ReadinessDashboard';
import NistCsfSspReport from './pages/NistCsfSspReport';
import './App.css';

// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  // Redirect clients to their special dashboard
  if (user?.role === 'client') {
    return <Navigate to="/client" replace />;
  }
  
  return children;
};

// Client-only route wrapper
const ClientRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  // Only clients can access client routes
  if (user?.role !== 'client') {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Public Route wrapper (redirects to dashboard if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  
  if (isAuthenticated) {
    // Redirect clients to client dashboard, others to regular dashboard
    if (user?.role === 'client') {
      return <Navigate to="/client" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Layout component that conditionally shows Navbar
const Layout = ({ children }) => {
  const location = useLocation();
  // Don't show navbar on login/register pages, shared results, client dashboard, or self-assessment
  const hideNavbar = ['/login', '/register', '/self-assessment'].includes(location.pathname) || 
                     location.pathname.startsWith('/shared/') ||
                     location.pathname.startsWith('/client');
  
  return (
    <>
      {!hideNavbar && <Navbar />}
      {children}
    </>
  );
};

function AppRoutes() {
  return (
    <Layout>
    <Routes>
      {/* Public Routes */}
      <Route 
        path="/" 
        element={
          <PublicRoute>
            <Landing />
          </PublicRoute>
        } 
      />
      <Route 
        path="/login" 
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        } 
      />
      <Route 
        path="/register" 
        element={
          <PublicRoute>
            <Register />
          </PublicRoute>
        } 
      />
      
      {/* Self-Assessment - Public (no auth needed for taking survey) */}
      <Route path="/self-assessment" element={<SelfAssessment />} />
      
      {/* Self-Assessment Results - Protected (requires auth to view results) */}
      <Route 
        path="/self-assessment/results" 
        element={
          <ProtectedRoute>
            <SelfAssessmentResults />
          </ProtectedRoute>
        } 
      />
      
      {/* Shared Results - Public (no auth needed) */}
      <Route path="/shared/:token" element={<SharedResults />} />
      
      {/* Client Dashboard */}
      <Route 
        path="/client" 
        element={
          <ClientRoute>
            <ClientDashboard />
          </ClientRoute>
        } 
      />
      
      {/* Protected Routes (regular users and admins) */}
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/assessment/:id" 
        element={
          <ProtectedRoute>
            <Assessment />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/assessment/:id/readiness" 
        element={
          <ProtectedRoute>
            <ReadinessDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/assessment/:id/ssp/nist-csf" 
        element={
          <ProtectedRoute>
            <NistCsfSspReport />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/report/:id" 
        element={
          <ProtectedRoute>
            <Report />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin" 
        element={
          <ProtectedRoute>
            <AdminDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/question-cms" 
        element={
          <ProtectedRoute>
            <QuestionCMS />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/tenants" 
        element={
          <ProtectedRoute>
            <SuperAdminDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/resources" 
        element={
          <ProtectedRoute>
            <Resources />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/news" 
        element={
          <ProtectedRoute>
            <NewsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/findings/:id" 
        element={
          <ProtectedRoute>
            <Findings />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/ssp" 
        element={
          <ProtectedRoute>
            <SSPList />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/ssp/:id" 
        element={
          <ProtectedRoute>
            <SSPEditor />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/vulnerabilities" 
        element={
          <ProtectedRoute>
            <VulnerabilityDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/scorecard" 
        element={
          <ProtectedRoute>
            <SecurityScorecard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/calendar" 
        element={
          <ProtectedRoute>
            <ComplianceCalendar />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/incidents" 
        element={
          <ProtectedRoute>
            <IncidentTracker />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/risks" 
        element={
          <ProtectedRoute>
            <RiskRegister />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/policies" 
        element={
          <ProtectedRoute>
            <PolicyLibrary />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/assets" 
        element={
          <ProtectedRoute>
            <AssetInventory />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/virtual-ciso" 
        element={
          <ProtectedRoute>
            <VirtualCISO />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/service-desk" 
        element={
          <ProtectedRoute>
            <TicketDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/qbr" 
        element={
          <ProtectedRoute>
            <QBR />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/msp" 
        element={
          <ProtectedRoute>
            <MSPPortfolio />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/proposals" 
        element={
          <ProtectedRoute>
            <ProposalGenerator />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/proposals/:proposalId" 
        element={
          <ProtectedRoute>
            <ProposalGenerator />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/contracts" 
        element={
          <ProtectedRoute>
            <ContractManagement />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/contracts/:contractId" 
        element={
          <ProtectedRoute>
            <ContractManagement />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/pentest" 
        element={
          <ProtectedRoute>
            <PentestDashboard />
          </ProtectedRoute>
        } 
      />
      
      {/* Organization Routes */}
      <Route 
        path="/organizations/settings" 
        element={
          <ProtectedRoute>
            <OrganizationSettings />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/organizations/new" 
        element={
          <ProtectedRoute>
            <CreateOrganization />
          </ProtectedRoute>
        } 
      />
      
      {/* Alerts */}
      <Route 
        path="/alerts" 
        element={
          <ProtectedRoute>
            <AlertsPage />
          </ProtectedRoute>
        } 
      />

      {/* GRC Suite Routes */}
      <Route 
        path="/grc/training" 
        element={
          <ProtectedRoute>
            <TrainingDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/grc/vendors" 
        element={
          <ProtectedRoute>
            <VendorRiskDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/grc/darkweb" 
        element={
          <ProtectedRoute>
            <DarkWebDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/grc/controls" 
        element={
          <ProtectedRoute>
            <ControlMappingDashboard />
          </ProtectedRoute>
        } 
      />
      
      {/* Evidence Library */}
      <Route 
        path="/evidence" 
        element={
          <ProtectedRoute>
            <EvidenceLibrary />
          </ProtectedRoute>
        } 
      />
      
      {/* Enhanced Data Room */}
      <Route 
        path="/dataroom" 
        element={
          <ProtectedRoute>
            <DataRoomPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/dataroom/:clientId" 
        element={
          <ProtectedRoute>
            <DataRoomPage />
          </ProtectedRoute>
        } 
      />
      
      {/* SSP Report - Keystone Alliance */}
      <Route 
        path="/ssp-report" 
        element={
          <ProtectedRoute>
            <SspReport />
          </ProtectedRoute>
        } 
      />
      
      {/* SSP Full Report - Printable Web View */}
      <Route 
        path="/ssp-full-report" 
        element={
          <ProtectedRoute>
            <SspFullReport />
          </ProtectedRoute>
        } 
      />
      
      {/* My SSP Reports - Stored Reports Library */}
      <Route 
        path="/my-ssp-reports" 
        element={
          <ProtectedRoute>
            <MySspReports />
          </ProtectedRoute>
        } 
      />
      
      {/* Catch-all redirect */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Layout>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <NotificationProvider>
            <AppRoutes />
          </NotificationProvider>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
